import { useState, useEffect, useCallback, useMemo } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  Select,
  Result,
  MathDiv,
  ActionButtonsRow,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  CopyHTMLButton,
  AlertDiv,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo, FiPlus, FiTrash2 } from 'react-icons/fi'
import { MdError } from 'react-icons/md'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
import { Table, Th, Td, Tr, TableContainer } from './styles'

const HISTORY_KEY = 'steel-wind-loads-history'
const MAX_HISTORY = 10

const TERRAIN_CATEGORIES = [
  { value: 1, label: 'Categoria I' },
  { value: 2, label: 'Categoria II' },
  { value: 3, label: 'Categoria III' },
  { value: 4, label: 'Categoria IV' },
  { value: 5, label: 'Categoria V' },
]

const TOPOGRAPHIES = [
  { value: 'flat', label: 'Terreno plano ou fracamente acidentado' },
  { value: 'valley', label: 'Vale profundo protegido' },
  { value: 'slope', label: 'Talude ou morro' },
]

const CPI_MODES = [
  { value: 'direct', label: 'Cpi direto' },
  { value: 'positive-ratio', label: 'Interpolado (razão positiva)' },
  { value: 'negative-ratio', label: 'Interpolado (razão negativa)' },
]

const NUMBER_PATTERN = /^[+-]?(\d+\.?\d*|\.\d+)$/

let caseUid = 0
function newCase(index) {
  caseUid += 1
  return { uid: caseUid, id: `caso-${index}`, label: '', cpe: '', cpiMode: 'direct', cpi: '', ratio: '' }
}

// Texto vazio vira null (não zero); aceita vírgula decimal
function parseNumber(value) {
  if (value === null || value === undefined) return null
  const text = String(value).trim().replace(',', '.')
  if (text === '' || !NUMBER_PATTERN.test(text)) return null
  const num = Number(text)
  return Number.isFinite(num) ? num : null
}

function fmt(value, decimals = 3) {
  return Number(value).toLocaleString('pt-BR', { maximumFractionDigits: decimals })
}

function validateNumber(value, { min, minExclusive, max } = {}) {
  const text = String(value ?? '').trim()
  if (text === '') return 'Campo obrigatório'
  const num = parseNumber(text)
  if (num === null) return 'Valor deve ser um número'
  if (minExclusive !== undefined && num <= minExclusive) return `Valor deve ser maior que ${minExclusive}`
  if (min !== undefined && num < min) return `Valor deve ser maior ou igual a ${min}`
  if (max !== undefined && num > max) return `Valor deve ser menor ou igual a ${max}`
  return null
}

export function SteelWindLoads() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [basicWindSpeed, setBasicWindSpeed] = useState('')

  const [width, setWidth] = useState('')
  const [length, setLength] = useState('')
  const [eaveHeight, setEaveHeight] = useState('')
  const [roofHeight, setRoofHeight] = useState('0')
  const [frameSpacing, setFrameSpacing] = useState('')

  const [topographyType, setTopographyType] = useState('flat')
  const [slopeAngle, setSlopeAngle] = useState('')
  const [slopeHeight, setSlopeHeight] = useState('')
  const [hillHeight, setHillHeight] = useState('')

  const [terrainCategory, setTerrainCategory] = useState(2)
  const [buildingClass, setBuildingClass] = useState('A')
  const [statisticalGroup, setStatisticalGroup] = useState(2)

  const [cases, setCases] = useState([])

  const [result, setResult] = useState(null)
  const [data, setData] = useState({})
  const [resultKey, setResultKey] = useState('')
  const [resultLabels, setResultLabels] = useState({})
  const [apiError, setApiError] = useState('')
  const [isFormValid, setIsFormValid] = useState(false)
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  useEffect(() => {
    const saved = localStorage.getItem(HISTORY_KEY)
    if (saved) {
      try {
        setHistory(JSON.parse(saved))
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((entry) => {
    setHistory((prev) => {
      const updated = [{ ...entry, timestamp: new Date().toISOString(), id: Date.now() }, ...prev].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(updated))
      return updated
    })
    setCurrentHistoryIndex(0)
  }, [])

  function applyState(state) {
    setBasicWindSpeed(state.basicWindSpeed ?? '')
    setWidth(state.width ?? '')
    setLength(state.length ?? '')
    setEaveHeight(state.eaveHeight ?? '')
    setRoofHeight(state.roofHeight ?? '0')
    setFrameSpacing(state.frameSpacing ?? '')
    setTopographyType(state.topographyType ?? 'flat')
    setSlopeAngle(state.slopeAngle ?? '')
    setSlopeHeight(state.slopeHeight ?? '')
    setHillHeight(state.hillHeight ?? '')
    setTerrainCategory(state.terrainCategory ?? 2)
    setBuildingClass(state.buildingClass ?? 'A')
    setStatisticalGroup(state.statisticalGroup ?? 2)
    setCases(
      (state.cases ?? []).map((c) => {
        caseUid += 1
        return { ...c, uid: caseUid }
      })
    )
    setData(state.data ?? {})
    setResult(state.result ?? null)
    setResultKey(state.resultKey ?? '')
    setResultLabels(state.resultLabels ?? {})
    setApiError('')
    setTouched({})
  }

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return
      applyState(history[index])
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) loadFromHistory(newIndex)
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  useEffect(() => {
    const newErrors = {}

    newErrors.basicWindSpeed = validateNumber(basicWindSpeed, { minExclusive: 0 })
    newErrors.width = validateNumber(width, { minExclusive: 0 })
    newErrors.length = validateNumber(length, { minExclusive: 0 })
    if (!newErrors.width && !newErrors.length && parseNumber(length) < parseNumber(width)) {
      newErrors.length = 'Comprimento deve ser maior ou igual à largura'
    }
    newErrors.eaveHeight = validateNumber(eaveHeight, { minExclusive: 0 })
    newErrors.roofHeight = validateNumber(roofHeight, { min: 0 })
    newErrors.frameSpacing = validateNumber(frameSpacing, { minExclusive: 0 })

    if (topographyType === 'slope') {
      newErrors.slopeAngle = validateNumber(slopeAngle, { min: 0, max: 90 })
      newErrors.slopeHeight = validateNumber(slopeHeight, { min: 0 })
      newErrors.hillHeight = validateNumber(hillHeight, { minExclusive: 0 })
    }

    const seen = new Set()
    cases.forEach((c, i) => {
      const id = c.id.trim()
      if (id === '') newErrors[`case.${i}.id`] = 'Campo obrigatório'
      else if (seen.has(id)) newErrors[`case.${i}.id`] = 'ID duplicado'
      seen.add(id)

      newErrors[`case.${i}.cpe`] = validateNumber(c.cpe)
      if (c.cpiMode === 'direct') newErrors[`case.${i}.cpi`] = validateNumber(c.cpi)
      else newErrors[`case.${i}.ratio`] = validateNumber(c.ratio, { min: 0 })
    })

    setErrors(newErrors)
    setIsFormValid(!Object.values(newErrors).some(Boolean))
  }, [
    basicWindSpeed,
    width,
    length,
    eaveHeight,
    roofHeight,
    frameSpacing,
    topographyType,
    slopeAngle,
    slopeHeight,
    hillHeight,
    cases,
  ])

  const handleBlur = (fieldName) => setTouched((prev) => ({ ...prev, [fieldName]: true }))

  function buildPayload() {
    const topography =
      topographyType === 'slope'
        ? {
            type: 'slope',
            angle: parseNumber(slopeAngle),
            height: parseNumber(slopeHeight),
            hillHeight: parseNumber(hillHeight),
          }
        : { type: topographyType }

    return {
      basicWindSpeed: parseNumber(basicWindSpeed),
      geometry: {
        width: parseNumber(width),
        length: parseNumber(length),
        eaveHeight: parseNumber(eaveHeight),
        roofHeight: parseNumber(roofHeight),
        frameSpacing: parseNumber(frameSpacing),
      },
      topography,
      terrainCategory: Number(terrainCategory),
      buildingClass,
      statisticalGroup: Number(statisticalGroup),
      pressureCases: cases.map((c) => ({
        id: c.id.trim(),
        cpe: parseNumber(c.cpe),
        cpi: c.cpiMode === 'direct' ? parseNumber(c.cpi) : { mode: c.cpiMode, ratio: parseNumber(c.ratio) },
      })),
    }
  }

  // Chave do payload atual; usada para marcar o resultado como anterior após edições
  const currentKey = useMemo(
    () => (isFormValid ? JSON.stringify(buildPayload()) : 'invalid'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isFormValid,
      basicWindSpeed,
      width,
      length,
      eaveHeight,
      roofHeight,
      frameSpacing,
      topographyType,
      slopeAngle,
      slopeHeight,
      hillHeight,
      terrainCategory,
      buildingClass,
      statisticalGroup,
      cases,
    ]
  )
  const isStale = result !== null && currentKey !== resultKey

  function formState() {
    return {
      basicWindSpeed,
      width,
      length,
      eaveHeight,
      roofHeight,
      frameSpacing,
      topographyType,
      slopeAngle,
      slopeHeight,
      hillHeight,
      terrainCategory,
      buildingClass,
      statisticalGroup,
      cases: cases.map((c) => ({
        id: c.id,
        label: c.label,
        cpe: c.cpe,
        cpiMode: c.cpiMode,
        cpi: c.cpi,
        ratio: c.ratio,
      })),
    }
  }

  async function handleCalculate(event) {
    event.preventDefault()
    if (isLoading) return

    const hasExistingResults = result !== null
    setIsLoading(true)
    setApiError('')
    if (hasExistingResults) setIsRecalculating(true)

    try {
      const payload = buildPayload()
      const key = JSON.stringify(payload)
      const labels = {}
      cases.forEach((c) => {
        labels[c.id.trim()] = c.label.trim()
      })

      const response = await api.post('/structural/wind-loads', payload)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }
      await fetchRateLimitStatus()

      setData(payload)
      setResult(response.data)
      setResultKey(key)
      setResultLabels(labels)
      saveToHistory({
        ...formState(),
        data: payload,
        result: response.data,
        resultKey: key,
        resultLabels: labels,
      })
      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)
      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }
      if (error.response?.status === 400 && error.response?.data?.message) {
        setApiError(error.response.data.message)
        toast.error('Entradas inválidas. Verifique os campos.')
        return
      }
      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) setTimeout(() => setIsRecalculating(false), 500)
    }
  }

  function handleCopyResults() {
    copyResultsToClipboard({ result })
  }

  function handleSave() {
    const stateData = { ...formState(), data, result, resultKey, resultLabels }
    const blob = new Blob([JSON.stringify(stateData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Aço Cargas de Vento.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        applyState(JSON.parse(ev.target.result))
        setCurrentHistoryIndex(-1)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
    event.target.value = ''
  }

  function updateCase(uid, changes) {
    setCases((prev) => prev.map((c) => (c.uid === uid ? { ...c, ...changes } : c)))
  }

  function addCase() {
    setCases((prev) => [...prev, newCase(prev.length + 1)])
  }

  function removeCase(uid) {
    setCases((prev) => prev.filter((c) => c.uid !== uid))
  }

  function textInput(name, value, onChange, numeric = true) {
    return (
      <ValidatedInput
        name={name}
        type="text"
        inputMode={numeric ? 'decimal' : 'text'}
        value={value}
        onChange={(ev) => onChange(ev.target.value)}
        errors={errors}
        touched={touched}
        onBlur={handleBlur}
        disabled={isLoading}
      />
    )
  }

  function caseLabel(id) {
    const label = resultLabels[id]
    return label ? `${label} (${id})` : id
  }

  function cpiDescription(caseResult) {
    const cpiInput = data?.pressureCases?.find((c) => c.id === caseResult.id)?.cpi
    if (cpiInput && typeof cpiInput === 'object') {
      const kind = cpiInput.mode === 'positive-ratio' ? 'positiva' : 'negativa'
      return `interpolado, razão ${kind} = ${fmt(cpiInput.ratio)}`
    }
    return 'direto'
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #2c3e50 0%, #34495e 100%)">
        <div className="header-content">
          <BackButton to="/aco">
            <FiArrowLeft /> Voltar para Aço
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/aco">Aço</a>
            <span>/</span>
            <span>Cargas de vento</span>
          </div>
          <h1>🌬️ Cargas de vento</h1>
          <p className="subtitle">
            Pressão dinâmica e cargas lineares de vento em pórticos a partir dos fatores S1, S2 e S3
          </p>
          <div className="badges">
            <Badge>📘 NBR 6123:1988 (referência histórica)</Badge>
            <Badge>🧮 Método VisualVentos 2.0.2</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #e8f4f8 0%, #cfe9f3 100%)"
        $border="2px solid #a8d8ea"
        $primaryColor="#2c3e50"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta calculadora</h3>
            <p>
              Calcula a geometria derivada, os fatores S1, S2 e S3, a velocidade característica, a pressão dinâmica e as
              cargas lineares para cada par Cpe/Cpi informado. O método é o núcleo do VisualVentos 2.0.2, baseado na NBR
              6123/1988; não é um cálculo completo segundo a edição atual da norma.
            </p>
            <p>
              A velocidade básica V0 e os coeficientes Cpe e Cpi (ou a razão para interpolar Cpi) são informados por
              você. Não há seleção automática de coeficientes, consulta de V0 por município nem dimensionamento de
              elementos.
            </p>
            <div className="norms">
              <span>NBR 6123:1988</span>
              <span>VisualVentos 2.0.2</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />
          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculate}>
            <h3>Vento</h3>
            <InputDiv>
              <span>Velocidade básica V0 (m/s)</span>
              {textInput('basicWindSpeed', basicWindSpeed, setBasicWindSpeed)}
            </InputDiv>

            <h3>Geometria</h3>
            <InputDiv>
              <span>Largura b — menor dimensão em planta (m)</span>
              {textInput('width', width, setWidth)}
            </InputDiv>
            <InputDiv>
              <span>Comprimento a — maior dimensão em planta (m)</span>
              {textInput('length', length, setLength)}
            </InputDiv>
            <InputDiv>
              <span>Altura do beiral h (m)</span>
              {textInput('eaveHeight', eaveHeight, setEaveHeight)}
            </InputDiv>
            <InputDiv>
              <span>Altura da cobertura h1 acima do beiral (m)</span>
              {textInput('roofHeight', roofHeight, setRoofHeight)}
            </InputDiv>
            <InputDiv>
              <span>Espaçamento entre pórticos (m)</span>
              {textInput('frameSpacing', frameSpacing, setFrameSpacing)}
            </InputDiv>

            <h3>Topografia (S1)</h3>
            <InputDiv>
              <span>Tipo de terreno</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  value={topographyType}
                  onChange={(ev) => setTopographyType(ev.target.value)}
                  disabled={isLoading}
                >
                  {TOPOGRAPHIES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            {topographyType === 'slope' && (
              <>
                <InputDiv>
                  <span>Inclinação do talude ou morro (°)</span>
                  {textInput('slopeAngle', slopeAngle, setSlopeAngle)}
                </InputDiv>
                <InputDiv>
                  <span>Altura z do ponto considerado (m)</span>
                  {textInput('slopeHeight', slopeHeight, setSlopeHeight)}
                </InputDiv>
                <InputDiv>
                  <span>Altura d do talude ou morro (m)</span>
                  {textInput('hillHeight', hillHeight, setHillHeight)}
                </InputDiv>
              </>
            )}

            <h3>Rugosidade e fator estatístico (S2 e S3)</h3>
            <InputDiv>
              <span>Categoria do terreno</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  value={terrainCategory}
                  onChange={(ev) => setTerrainCategory(Number(ev.target.value))}
                  disabled={isLoading}
                >
                  {TERRAIN_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            <InputDiv>
              <span>Classe da edificação</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  value={buildingClass}
                  onChange={(ev) => setBuildingClass(ev.target.value)}
                  disabled={isLoading}
                >
                  {['A', 'B', 'C'].map((c) => (
                    <option key={c} value={c}>
                      Classe {c}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            <InputDiv>
              <span>Grupo estatístico</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  value={statisticalGroup}
                  onChange={(ev) => setStatisticalGroup(Number(ev.target.value))}
                  disabled={isLoading}
                >
                  {[1, 2, 3, 4, 5].map((g) => (
                    <option key={g} value={g}>
                      Grupo {g}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>

            <h3>Casos de pressão</h3>
            <p style={{ marginBottom: '10px' }}>
              Cada caso é um par Cpe/Cpi. Para dois Cpi na mesma região, adicione dois casos. Sem casos, são calculados
              apenas os fatores, a velocidade e a pressão dinâmica.
            </p>
            {cases.map((c, i) => (
              <div
                key={c.uid}
                style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px' }}
              >
                <InputDiv>
                  <span>ID do caso</span>
                  {textInput(`case.${i}.id`, c.id, (v) => updateCase(c.uid, { id: v }), false)}
                </InputDiv>
                <InputDiv>
                  <span>Rótulo (opcional, só nesta tela)</span>
                  <input
                    type="text"
                    value={c.label}
                    onChange={(ev) => updateCase(c.uid, { label: ev.target.value })}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Cpe</span>
                  {textInput(`case.${i}.cpe`, c.cpe, (v) => updateCase(c.uid, { cpe: v }))}
                </InputDiv>
                <InputDiv>
                  <span>Cpi</span>
                  <div>
                    <Select
                      style={{ marginLeft: '0' }}
                      value={c.cpiMode}
                      onChange={(ev) => updateCase(c.uid, { cpiMode: ev.target.value })}
                      disabled={isLoading}
                    >
                      {CPI_MODES.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                </InputDiv>
                {c.cpiMode === 'direct' ? (
                  <InputDiv>
                    <span>Valor de Cpi</span>
                    {textInput(`case.${i}.cpi`, c.cpi, (v) => updateCase(c.uid, { cpi: v }))}
                  </InputDiv>
                ) : (
                  <InputDiv>
                    <span>Razão adimensional (≥ 0)</span>
                    {textInput(`case.${i}.ratio`, c.ratio, (v) => updateCase(c.uid, { ratio: v }))}
                  </InputDiv>
                )}
                <button
                  type="button"
                  onClick={() => removeCase(c.uid)}
                  disabled={isLoading}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  <FiTrash2 /> Remover caso
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addCase}
              disabled={isLoading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '16px' }}
            >
              <FiPlus /> Adicionar caso
            </button>

            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('wind-loads-file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input
              type="file"
              id="wind-loads-file-input"
              accept=".json"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </form>
          {apiError && (
            <AlertDiv style={{ marginTop: '12px' }}>
              <span>{apiError}</span>
              <MdError />
            </AlertDiv>
          )}
        </Card>

        {result !== null && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              {isStale && (
                <AlertDiv>
                  <span>
                    As entradas foram alteradas: este resultado é anterior. Calcule novamente para atualizá-lo.
                  </span>
                  <MdError />
                </AlertDiv>
              )}
              <MathDiv>
                <BlockMath math={`S_1 = ${fmt(result.factors.s1, 4)}`} />
                <BlockMath math={`S_2 = ${fmt(result.factors.s2, 4)}`} />
                <BlockMath math={`S_3 = ${fmt(result.factors.s3, 4)}`} />
                <BlockMath math={`\\boxed{V_k = ${fmt(result.wind.characteristicSpeed)} \\, \\text{m/s}}`} />
                <BlockMath math={`\\boxed{q = ${fmt(result.wind.dynamicPressure, 4)} \\, \\text{kN/m}^2}`} />
              </MathDiv>
              {result.pressureCases.length === 0 ? (
                <p>Nenhum caso de carga configurado: não há cargas por região.</p>
              ) : (
                <TableContainer>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Caso</Th>
                        <Th>Cpe</Th>
                        <Th>Cpi</Th>
                        <Th>Cpe − Cpi</Th>
                        <Th>Pressão líquida (kN/m²)</Th>
                        <Th>Carga linear (kN/m)</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.pressureCases.map((c) => (
                        <Tr key={c.id}>
                          <Td>{caseLabel(c.id)}</Td>
                          <Td>{fmt(c.cpe)}</Td>
                          <Td>{fmt(c.cpi)}</Td>
                          <Td>{fmt(c.netCoefficient)}</Td>
                          <Td>{fmt(c.netPressure, 4)}</Td>
                          <Td>{fmt(c.linearLoad, 4)}</Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </TableContainer>
              )}
            </Result>

            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <p>Método: VisualVentos 2.0.2 (referência histórica NBR 6123/1988). Valores calculados pelo servidor.</p>

              <MathDiv>
                <h3>Geometria derivada</h3>
                <BlockMath math={`a_1 = ${fmt(result.geometry.a1)} \\, \\text{m}`} />
                <BlockMath math={`a_2 = ${fmt(result.geometry.a2)} \\, \\text{m}`} />
                <BlockMath math={`b_1 = ${fmt(result.geometry.b1)} \\, \\text{m}`} />
                <BlockMath math={`\\text{Faixa de borda} = ${fmt(result.geometry.edgeStripWidth)} \\, \\text{m}`} />
                <BlockMath math={`\\text{Ângulo da cobertura} = ${fmt(result.geometry.roofAngle)}^\\circ`} />

                <h3>Fator topográfico S1</h3>
                {result.factors.topography.type === 'slope' ? (
                  <BlockMath
                    math={`\\text{Talude/morro: } \\theta = ${fmt(result.factors.topography.angle)}^\\circ, \\; z = ${fmt(result.factors.topography.height)} \\, \\text{m}, \\; d = ${fmt(result.factors.topography.hillHeight)} \\, \\text{m}`}
                  />
                ) : (
                  <p>
                    {result.factors.topography.type === 'valley'
                      ? 'Vale profundo protegido'
                      : 'Terreno plano ou fracamente acidentado'}
                  </p>
                )}
                <BlockMath math={`\\boxed{S_1 = ${fmt(result.factors.s1, 4)}}`} />

                <h3>Fator de rugosidade S2</h3>
                <p>
                  Categoria {result.factors.terrainCategory}, classe {result.factors.buildingClass}
                </p>
                <BlockMath math={`S_2 = b \\cdot F_r \\cdot \\left(0,1 \\cdot z\\right)^{p}`} />
                <BlockMath
                  math={`S_2 = ${fmt(result.factors.roughness.b, 4)} \\cdot ${fmt(result.factors.roughness.fr, 4)} \\cdot \\left(0,1 \\cdot ${fmt(result.factors.roughness.referenceHeight)}\\right)^{${fmt(result.factors.roughness.p, 4)}}`}
                />
                <BlockMath math={`\\boxed{S_2 = ${fmt(result.factors.s2, 4)}}`} />

                <h3>Fator estatístico S3</h3>
                <p>Grupo {result.factors.statisticalGroup}</p>
                <BlockMath math={`\\boxed{S_3 = ${fmt(result.factors.s3, 4)}}`} />

                <h3>Velocidade característica</h3>
                <BlockMath math={`V_k = V_0 \\cdot S_1 \\cdot S_2 \\cdot S_3`} />
                <BlockMath
                  math={`V_k = ${fmt(result.wind.basicWindSpeedUsed, 4)} \\cdot ${fmt(result.factors.s1, 4)} \\cdot ${fmt(result.factors.s2, 4)} \\cdot ${fmt(result.factors.s3, 4)}`}
                />
                <BlockMath math={`\\boxed{V_k = ${fmt(result.wind.characteristicSpeed)} \\, \\text{m/s}}`} />

                <h3>Pressão dinâmica</h3>
                <BlockMath math={`q = 0,613 \\cdot 10^{-3} \\cdot V_k^2`} />
                <BlockMath math={`q = 0,613 \\cdot 10^{-3} \\cdot ${fmt(result.wind.characteristicSpeed)}^2`} />
                <BlockMath math={`\\boxed{q = ${fmt(result.wind.dynamicPressure, 4)} \\, \\text{kN/m}^2}`} />

                <h3>Cargas por caso</h3>
                {result.pressureCases.length === 0 && <p>Nenhum caso de carga configurado.</p>}
                {result.pressureCases.map((c) => (
                  <div key={c.id}>
                    <h4>{caseLabel(c.id)}</h4>
                    <p>
                      Cpe = {fmt(c.cpe)}; Cpi = {fmt(c.cpi)} ({cpiDescription(c)})
                    </p>
                    <BlockMath
                      math={`p = (C_{pe} - C_{pi}) \\cdot q = (${fmt(c.cpe)} - (${fmt(c.cpi)})) \\cdot ${fmt(result.wind.dynamicPressure, 4)} = ${fmt(c.netPressure, 4)} \\, \\text{kN/m}^2`}
                    />
                    <BlockMath
                      math={`\\boxed{w = p \\cdot e = ${fmt(c.netPressure, 4)} \\cdot ${fmt(result.geometry.frameSpacing)} = ${fmt(c.linearLoad, 4)} \\, \\text{kN/m}}`}
                    />
                  </div>
                ))}
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
